import initSqlJs, { type Database } from 'sql.js';

export interface AddressSuggestion {
  objectId: number;
  name: string;
}

let db: Database | null = null;
let loading: Promise<void> | null = null;

async function ensureDb(): Promise<Database> {
  if (db) return db;
  if (loading) {
    await loading;
    return db!;
  }

  loading = (async () => {
    const SQL = await initSqlJs({
      locateFile: (file: string) => file.endsWith('.wasm') ? '/app/assets/sql-wasm.wasm' : `/app/assets/${file}`,
    });

    const resp = await fetch('/data/addresses.db');
    if (!resp.ok) throw new Error('Failed to load address database');
    const buf = await resp.arrayBuffer();
    db = new SQL.Database(new Uint8Array(buf));
  })();

  try {
    await loading;
  } catch (e) {
    loading = null; // allow retry on next call
    throw e;
  }
  return db!;
}

let prefetchInflight: Promise<void> | null = null;

/**
 * v0.79.41 — eager warm-up of the FIAS SW cache.
 *
 * 10.4 MB SQLite blob, served once per device per 30 days (Workbox `fias-db`
 * CacheFirst, vite.config.ts:77). Lazy-loaded by `ensureDb()` only when
 * `searchCities/Streets/Buildings` first runs — i.e. when the player is
 * physically at a quest location, on possibly-weak cellular, mid-input.
 * Worst possible moment for a 9-30s blocking spinner.
 *
 * Prefetch contract: fire-and-forget, no return value. Idempotent — repeat
 * calls in the same session are no-ops via the `prefetchInflight` guard or
 * the already-loaded `db` short-circuit. Failures are silent — the lazy
 * `ensureDb()` retry path handles offline/error cases.
 *
 * Body is drained (`resp.arrayBuffer()`) so Workbox's `cache.put(..., clone)`
 * has time to commit before the response object is GC'd. The buffer itself
 * goes nowhere — sql.js stays lazy until first real search.
 *
 * Wired in main.ts (cold-mount rejoin) and join-view.ts (fresh join).
 * Player-only — spectators never enter addresses.
 */
export function prefetchAddressDb(): void {
  if (db) return;
  if (prefetchInflight) return;
  prefetchInflight = (async () => {
    try {
      const resp = await fetch('/data/addresses.db', { priority: 'low' } as RequestInit);
      if (!resp.ok) return;
      await resp.arrayBuffer();
    } catch {
      // Network failure or aborted — ensureDb() will retry lazily on first
      // searchCities call. No state to clean up: prefetchInflight is reset
      // in the finally below, so a future foreground call can try again.
    }
  })();
  prefetchInflight.finally(() => { prefetchInflight = null; });
}

// SQLite COLLATE NOCASE only handles ASCII, not Cyrillic.
// FIAS names are Title Case (Карла Маркса). Normalize: "ЛЕНИНА" → "Ленина", "карла маркса" → "Карла Маркса"
function capitalizeFirst(s: string): string {
  return s.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

// Map full Russian street type names to FIAS abbreviations
const typeAbbreviations: Record<string, string> = {
  'улица': 'ул.', 'проспект': 'пр-кт', 'переулок': 'пер.',
  'проезд': 'пр-д', 'бульвар': 'б-р', 'площадь': 'пл.',
  'тупик': 'туп.', 'шоссе': 'ш.', 'набережная': 'наб.'
};

export function normalizeTypeQuery(raw: string): string {
  const lower = raw.toLowerCase().replace(/\.$/, '');
  for (const [full, abbr] of Object.entries(typeAbbreviations)) {
    const abbrBase = abbr.replace(/\.$/, '').replace(/-/g, '');
    if (full.startsWith(lower) || lower.startsWith(full)) return abbr;
    if (abbrBase.startsWith(lower) || lower.startsWith(abbrBase)) return abbr;
  }
  return raw;
}

export async function searchCities(query: string): Promise<AddressSuggestion[]> {
  const database = await ensureDb();
  const normalized = capitalizeFirst(query);
  // FIAS data has ~133 duplicate settlement names in Чувашия. Always show typename
  // ("д. Новые Выселки") and, for duplicates, append the first street as a tiebreaker.
  // Mirrors backend AddressService.suggestCities.
  // For duplicate names (133 in Чувашия) append the containing district:
  // "д. Липово (Козловский район)" vs "д. Липово (Чебоксарский район)".
  // The cities.district column is filled in at DB build time from the municipal
  // hierarchy. Mirrors backend AddressService.suggestCities.
  //
  // REPLACE(name, 'ё', 'е') makes the match yo/ye-insensitive.
  const stmt = database.prepare(
    `SELECT c.objectid,
            c.typename || ' ' || c.name ||
            CASE WHEN (SELECT COUNT(*) FROM cities c2 WHERE c2.name = c.name) > 1
                 THEN COALESCE(' (' || c.district || ')', '')
                 ELSE ''
            END AS display_name
     FROM cities c
     WHERE REPLACE(c.name, 'ё', 'е') LIKE REPLACE(?, 'ё', 'е')
     ORDER BY c.name, c.district, display_name
     LIMIT 10`
  );
  stmt.bind([`${normalized}%`]);

  const results: AddressSuggestion[] = [];
  while (stmt.step()) {
    const row = stmt.getAsObject();
    results.push({ objectId: Number(row.objectid), name: String(row.display_name) });
  }
  stmt.free();
  return results;
}

export async function searchStreets(cityObjectId: number, query: string): Promise<AddressSuggestion[]> {
  const database = await ensureDb();

  if (!query.trim()) {
    // Return first 10 streets with typename
    const stmt = database.prepare(
      `SELECT s.objectid, s.typename || ' ' || s.name as display_name
       FROM streets s JOIN cities c ON s.city_id = c.id
       WHERE c.objectid = ?
       ORDER BY s.name LIMIT 10`
    );
    stmt.bind([cityObjectId]);
    const results: AddressSuggestion[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push({ objectId: Number(row.objectid), name: String(row.display_name) });
    }
    stmt.free();
    return results;
  }

  // Smart search matching backend AddressService logic.
  // FIAS name patterns: simple (Ленина), initial (К.Маркса), double initial (А.Н.Крылова),
  // spaced initial (М.Е. Ефимова), full name (Максима Горького), titled (Академика ...), hyphenated
  const nameQuery = capitalizeFirst(query);
  const rawQuery = query.trim();
  const typeQuery = normalizeTypeQuery(rawQuery);
  const words = rawQuery.trim().split(' ').filter(w => w.length > 0);

  // Initial pattern: "к"→"К.%", "Кар"→"К.%", "Карла Мар"→"К.Мар%"
  // Only for short first word (≤4 chars = plausible abbreviated name)
  const NEVER = 'THIS_NEVER_MATCHES';
  let initialPattern = NEVER;
  if (words.length > 0 && words[0].length <= 4) {
    const firstInitial = words[0].charAt(0).toUpperCase();
    const rest = words.length > 1 ? words.slice(1).map(w => capitalizeFirst(w)).join(' ') : '';
    initialPattern = `${firstInitial}.${rest}%`;
  }

  // After-dot: "кр"→"%.Кр%", finds "Т.Кривова", "А.Н.Крылова"
  const afterDotPattern = nameQuery.length >= 2 ? `%.${nameQuery}%` : NEVER;

  // Broad contains for ≥5 chars: "Горького"→"%Горького%", "Ефимова"→"%Ефимова%"
  const broadContains = nameQuery.length >= 5 ? `%${nameQuery}%` : NEVER;

  // Multi-word last word: "Карла Маркса"→"%Маркса%"
  const multiWordContains = words.length > 1 ? `%${capitalizeFirst(words[words.length - 1])}%` : NEVER;

  // REPLACE(...,'ё','е') makes every LIKE yo/ye-insensitive — "Зелёная" matches "Зеленая"
  // and vice versa. Mirrors backend AddressService.suggestStreets.
  const stmt = database.prepare(
    `SELECT objectid, display_name FROM (
         SELECT s.objectid, s.typename || ' ' || s.name as display_name, 1 as priority
         FROM streets s JOIN cities c ON s.city_id = c.id
         WHERE c.objectid = ? AND (
             REPLACE(s.name, 'ё', 'е') LIKE REPLACE(?, 'ё', 'е')
             OR REPLACE(s.name, 'ё', 'е') LIKE REPLACE(?, 'ё', 'е')
             OR REPLACE(s.name, 'ё', 'е') LIKE REPLACE(?, 'ё', 'е')
         )
     UNION
         SELECT s.objectid, s.typename || ' ' || s.name, 2
         FROM streets s JOIN cities c ON s.city_id = c.id
         WHERE c.objectid = ? AND (
             s.typename LIKE ?
             OR REPLACE(s.typename || ' ' || s.name, 'ё', 'е') LIKE REPLACE(?, 'ё', 'е')
             OR REPLACE(s.name, 'ё', 'е') LIKE REPLACE(?, 'ё', 'е')
             OR REPLACE(s.name, 'ё', 'е') LIKE REPLACE(?, 'ё', 'е')
         )
     ) GROUP BY objectid ORDER BY priority, substr(display_name, instr(display_name, ' ') + 1) LIMIT 10`
  );
  stmt.bind([
    cityObjectId,
    `${nameQuery}%`,    // P1: name prefix
    initialPattern,      // P1: initial (К.%)
    afterDotPattern,     // P1: after dot (%.Кр%)
    cityObjectId,
    `${typeQuery}%`,     // P2: typename
    `${rawQuery}%`,      // P2: combined
    broadContains,       // P2: broad contains (≥5 chars)
    multiWordContains,   // P2: multi-word last word
  ]);

  const results: AddressSuggestion[] = [];
  while (stmt.step()) {
    const row = stmt.getAsObject();
    results.push({ objectId: Number(row.objectid), name: String(row.display_name) });
  }
  stmt.free();
  return results;
}

export async function searchBuildings(streetObjectId: number, query: string): Promise<AddressSuggestion[]> {
  const database = await ensureDb();
  // GROUP BY number collapses FIAS duplicates (historical + current records
  // with the same house number on the same street). MIN(objectid) picks a
  // stable representative. Matches backend AddressService.suggestBuildings.
  const stmt = database.prepare(
    `SELECT MIN(b.objectid) AS objectid, b.number FROM buildings b
     JOIN streets s ON b.street_id = s.id
     WHERE s.objectid = ? AND b.number LIKE ?
     GROUP BY b.number
     ORDER BY CAST(b.number AS INTEGER), b.number LIMIT 15`
  );
  stmt.bind([streetObjectId, `${query}%`]);

  const results: AddressSuggestion[] = [];
  while (stmt.step()) {
    const row = stmt.getAsObject();
    results.push({ objectId: Number(row.objectid), name: String(row.number) });
  }
  stmt.free();
  return results;
}
